// @ts-check
'use_strict';

/**
 * UI object to display the yard letter preview.
 *
 * @property {Record} color_squence handles the generation of color sequences by the type.
 * @property {Object} elements container for the html elements bound on the page.
 * @property {MutationObserver} preview_observer the obsever that will update the preview on changes.
 */
export class YardLetterPreview {
    private color_squence: Readonly<Record<string, (letters:number) => Array<string>>> = {
        Multicolor: (letters:number) => {
            const sequence:Array<string> = (window as any).js_config.configurator.colors.sequences.multicolor;
            const priority:Record<string, number> = (window as any).js_config.configurator.colors.priorities.multicolor;

            const result:Array<string> = [];

            // If the sequence is less than a full rainbow, use the priority list to "squish" the rainbow.
            if (letters < sequence.length) {
                // Gather the number of each color from the priority list that we need based on the number of letters.
                let loops:number = 0;
                let key:string = '';
                for (let i:number = 0; i < letters; i += 1) {
                    if (i > Object.keys(priority).length - 1 + loops) {
                        loops = loops + Object.keys(priority).length;
                    }

                    key = Object.keys(priority)[i - loops];
                    priority[key] += 1;
                }

                for (let i:number = 0; i < letters; i += Object.keys(priority).length) {
                    for (const color of sequence) {
                        if (priority[color] > 0) {
                            priority[color] -= 1;
                            result.push(color);
                        }
                    }
                }
            } else {
                let loops:number = 0;
                for (let i:number = 0; i < letters; i += 1) {
                    if (i > sequence.length - 1 + loops) {
                        loops = loops + sequence.length;
                    }

                    result.push(sequence[i - loops])
                }
            }

            return result;
        },
        Pastel: (letters:number) => {
            const sequence:Array<string> = (window as any).js_config.configurator.colors.sequences.pastel;
            const result:Array<string> = [];

            let loops:number = 0;
            for (let i:number = 0; i < letters; i += 1) {
                if (i > sequence.length - 1 + loops) {
                    loops = loops + sequence.length;
                }

                result.push(sequence[i - loops])
            }

            return result;
        }
    };

    private elements: {
        controls: {
            labels: HTMLCollectionOf<Element>;
            swatches: NodeListOf<HTMLDivElement>;
            bubble_font: HTMLInputElement;
        }
        config_ui: HTMLElement
        preview_anchor: ParentNode;
        preview: HTMLDivElement
    };

    private preview_observer: MutationObserver;

    /**
     * Initializes the class by binding to the letter elements and displays the initial preview.
     *
     */
     constructor() {
        this.load_fonts();

        this.elements = {
            controls: null,
            config_ui: document.getElementById('js-configurator-ui'),
            preview_anchor: document.querySelectorAll('.tv-yard-letter-heading')[0],
            preview: null
        };

        this.preview_observer = new MutationObserver(this.observe_queue.bind(this));
        this.observe_queue();

    }

    /**
     * Stops the observer before we update the preview so we don't get an infinite loop.
     * Then start observing again once the preview is updated.
     */
    private observe_queue(): void {
        this.preview_observer.disconnect();
        this.generate_preview();
        this.observe();
    }

    /**
     * Observe the configurator UI for user changes.
     */
    private observe(): void {
        this.preview_observer.observe(this.elements.config_ui, {subtree: true, childList: true});
    }

    /**
     * Create references to the DOM elements we need to interact with.
     */
    private bind(): void {
        this.elements.controls = {
            labels: document.getElementsByClassName("js-yard-letter-word-label"),
            swatches: document.querySelectorAll(".tv-yard-letter-word-input-label .tv-select__advanced-selected-label .tv-color-picker__swatch"),
            bubble_font: document.querySelector('input[value="Bubble"]')
        };

        this.elements.preview_anchor = document.querySelectorAll('.tv-yard-letter-heading')[0];
    }

    /**
     * Clear any preview elements so that a new one can be appended.
     */
    private clear_preview(): void {
        const preview:HTMLElement = document.getElementById('tv-yard-letter-preview');
        if (preview) {
            preview.innerHTML = '';
        }
    }

    /**
     * Create the element that will contain the letter preview and append it to the anchor
     * in the config UI.
     */
    private create_preview_element(): void {
        this.elements.preview = document.getElementById('tv-yard-letter-preview') as HTMLDivElement;
        if (this.elements.preview) {
            this.elements.preview.innerHTML = '';
        } else {
            this.elements.preview = document.createElement('div');
            this.elements.preview.id = 'tv-yard-letter-preview';
            this.elements.preview.classList.add('row-fluid');
        }

        this.elements.preview_anchor.parentNode.insertBefore(this.elements.preview, this.elements.preview_anchor);

        // Add the container for the disclaimer.
        if (!document.getElementById('tv-yard-letter-preview-message')) {
            const message = document.createElement('div');
            message.id = 'tv-yard-letter-preview-message';
            message.classList.add('tv-yard-letter-preview-message');
            message.innerText = (window as any).js_config.configurator.messages.yard_letter_preview;
            this.elements.preview_anchor.parentNode.insertBefore(message, this.elements.preview_anchor);
        }
    }

    /**
     * Renders a new preview based on the selections on the page.
     */
    private generate_preview(): void {
        this.bind();
        this.clear_preview();
        this.create_preview_element();

        type Word = {
            color:string,
            text:string
        };

        const words:Array<Word> = [];

        for (let label of this.elements.controls.labels) {
            let word:Word = {
                color: label.parentNode.querySelector('.tv-color-picker__label').textContent,
                text: label.textContent.match(/"([^"]+)"/)[1]
            }

            words.push(word);
        }

        const multicolor_letters:number = words.filter(word => word.color === 'Multicolor').reduce((letters, word) => letters + word.text.length, 0);
        const pastel_letters:number = words.filter(word => word.color === 'Pastel').reduce((letters, word) => letters + word.text.length, 0);

        const color_sets = {
            Multicolor: this.color_squence['Multicolor'](multicolor_letters),
            Pastel: this.color_squence['Pastel'](pastel_letters)
        };

       // Iterate over the words and generate the preview for each of them.
       for (let [idx, word] of words.entries()) {
            // Add a new span to the preview that will house the word.
            let span:HTMLSpanElement = this.elements.preview.appendChild(document.createElement('span'));
            span.classList.add('tv-yard-letter-preview-word');

            // Iterate over the letter in the word and add the each one to the word preview.
            for (let i:number = 0; i < word.text.length; i++) {
                let letter:HTMLSpanElement = document.createElement('span');
                letter.innerHTML = word.text.charAt(i);

                // Set the font to bubble if the control is checked.
                if (this.elements.controls.bubble_font.checked === true) {
                    letter.classList.add('tv-yard-letter-font-round');
                } else {
                    letter.classList.add('tv-yard-letter-font-std');
                }

                // For multi color or pastel color, assign colors based on the list.
                if (word.color === 'Multicolor' || word.color === 'Pastel') {
                    letter.style.color = color_sets[word.color].shift();
                    letter.classList.add('tv-yard-letter-preview-letter-hex');
                }
                // For soild colors that use hex, just use the hex.
                else if (this.elements.controls.swatches[idx].style.backgroundImage === 'none' || this.elements.controls.swatches[idx].style.backgroundImage === 'initial') {
                    letter.style.color = this.elements.controls.swatches[idx].style.backgroundColor;
                    letter.classList.add('tv-yard-letter-preview-letter-hex');
                // For letters using images, copy the background image from the swatch.
                } else {
                    letter.style.backgroundImage = this.elements.controls.swatches[idx].style.backgroundImage;
                    letter.classList.add('tv-yard-letter-preview-letter');
                }

                span.appendChild(letter);
            }
        }
    }

    /**
     * Load the ccs containing the webfonts for the yard letters.
     * Normally this is done through styles.tpl but we don't want to consume views on the license if we aren't using it.
     */
    private load_fonts(): void {
        const fontcss:HTMLLinkElement = document.createElement('link');
        // TODO configuration path.
        fontcss.href = 'design_tool/data/fonts/yardletters/LettersFontsKit.css';
        fontcss.rel = "stylesheet";

        document.getElementsByTagName('head')[0].appendChild(fontcss);
    }
}
